import { PageSection } from "../blocks/layout/PageSection";
import { GridColumn } from "../blocks/layout/GridColumn";
import { getFieldBehaviour } from "../config/fieldBehaviours";
import classNames from "classnames";
import { getFieldComponent } from "../config/fieldComponents";
import { ContentRenderer } from "./ContentRenderer";
import { FieldRenderer } from "./FieldRenderer";
import { isContentConfig } from "./contentConfig.types";
import { FieldConfig } from "./fieldConfig.types";
import { StageFieldsProps, StageRendererProps } from "./StageRenderer.types";

const NON_EDITABLE_TYPES = new Set(["statusbadge", "component"]);

/**
 * Whether a field renders disabled, which is not simply `!canEdit`: a badge and a custom
 * component are displays rather than inputs, so disabling them would grey a value that
 * was never editable. Exported because the designer's drag overlay draws a field outside
 * any stage, and a field in flight has to look exactly as it does in place.
 */
export function fieldDisabled(field: FieldConfig, canEdit: boolean) {
  return NON_EDITABLE_TYPES.has(field.type)
    ? undefined
    : !canEdit || field.readOnly;
}

/** A stage's fields and content in their grid, without the section around them. */
export function StageFields({
  fields,
  values,
  onChange,
  canEdit = true,
  errors,
  preview,
  wrapField,
}: StageFieldsProps) {
  const handleFieldChange = (
    field: FieldConfig,
    newValue: unknown
  ) => {
    const patch: Record<string, unknown> = { [field.name]: newValue };

    const behaviour = getFieldBehaviour(field.onChange?.behaviour);
    if (behaviour && field.onChange?.target) {
      patch[field.onChange.target] = behaviour(newValue, values);
    }

    onChange(patch);
  };

  return (
    <div className="row gy-3">
      {fields.map((item, index) => {
        const rendered = isContentConfig(item) ? (
          <ContentRenderer content={item} />
        ) : (
          <FieldRenderer
            field={item}
            value={values[item.name]}
            values={values}
            onChange={(value) => handleFieldChange(item, value)}
            error={errors?.[item.name]}
            disabled={fieldDisabled(item, canEdit)}
            asText={!canEdit || item.showAsText}
            preview={preview}
          />
        );

        return (
          <GridColumn
            // Keyed by position, not by name: two items with no name collide,
            // which is every pair of dividers and any two unbound fields.
            // sectionToStage drops config ids before the renderer sees them.
            key={index}
            span={item.span ?? { desktop: 12 }}
          >
            {wrapField ? wrapField(item, rendered, index) : rendered}
          </GridColumn>
        );
      })}
    </div>
  );
}

export function StageRenderer({ stage, flat, ...fieldProps }: StageRendererProps) {
  const HeaderComponent = getFieldComponent(stage.headerComponentKey);
  const [only] = stage.fields;
  const BareComponent =
    stage.bare && only && !isContentConfig(only) && only.type === "component"
      ? getFieldComponent(only.componentKey)
      : undefined;

  if (BareComponent) {
    return (
      <div className="d-flex flex-column flex-grow-1">
        <BareComponent />
      </div>
    );
  }

  return (
    <PageSection
      title={stage.title}
      helperText={stage.helperText}
      actions={HeaderComponent && <HeaderComponent />}
      className={classNames({
        "bg-transparent shadow-none rounded-0 p-0": flat,
      })}
    >
      <StageFields fields={stage.fields} {...fieldProps} />
    </PageSection>
  );
}
